import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../../../src/environments/environment';
import { UserService } from '../../../../../../src/app/core/auth/_services/user.service';
import { firstValueFrom, forkJoin, Observable, of, ReplaySubject, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AllConfigListResponse, ConfigSetting, PageSettingsData, UpdateConfigRequest, UpdateConfigResponse } from '../../../../../../src/app/core/configuration/_models/config-settings.model';
import { CustomerService } from '../../../../../../src/app/core/configuration/_services/customer.service';

const API_URL = environment.API_URL;
const API_CONFIG_URL = '/api/config';
const REFRESH_ENV_CONFIG = '/refresh-env-config';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  constructor(
    private readonly http: HttpClient,
    private readonly userService: UserService,
    private customerService: CustomerService,
  ) { }
  private pageSettingsMap = new Map<string, PageSettingsData>();
  private settingsLoaded$ = new ReplaySubject<void>(1);
  private allSettingsFetch$: Observable<AllConfigListResponse> | null = null;  // Holds the active HTTP request so concurrent calls can subscribe to the same exact network fetch
  private showAlertV2: boolean = false;

  getConfigData(): Observable<any> {
    return this.customerService.fetchEnterpriseId().pipe(
      switchMap(enterpriseId => {
        const url = `${API_URL}${API_CONFIG_URL}`;
        const fields = ['title', 'pageName', 'modifiedAt', 'updatedBy'].join(',');
        const params = new HttpParams()
          .set('enterpriseId', enterpriseId)
          .set('fields', fields);

        return this.http.get(url, {
          params,
          headers: this.userService.getHttpAuthHeader()
        });
      })
    );
  }

  getAllPageSettings(): void {
    if (!this.allSettingsFetch$) {
      const allConfigPageNames = 'tenantModuleAccess,alertDetailsPage,alertsPage,environmentConfiguration,tagDetails';
      this.allSettingsFetch$ = this.customerService.fetchEnterpriseId().pipe(
        switchMap(enterpriseId => {
          if (!enterpriseId) {
            return of({
              config: {
                tenantModuleAccess: {},
                alertDetailsPage: {},
                alertsPage: {},
                environmentConfiguration: {},
                tagDetails: {}
              }
            });
          }
          const url = `${API_URL}${API_CONFIG_URL}`;
          const params = new HttpParams()
            .set('enterpriseId', enterpriseId)
            .set('pageName', allConfigPageNames)
            .set('fields', 'settings');
          return this.http.get<AllConfigListResponse>(url, { params, headers: this.userService.getHttpAuthHeader() });
        }),
        tap((data: AllConfigListResponse) => {
          let configData = data.config;
          // Expecting response in the format:
          // { tenantModuleAccess: {...}, alertDetailsPage: {...}, alertsPage: {...}, environmentConfiguration: {...} }
          Object.entries(configData).forEach(([pageName, config]) => {
            this.pageSettingsMap.set(pageName, config);
          });
          this.settingsLoaded$.next();
        }),
        shareReplay(1),
        catchError(err => {
          this.allSettingsFetch$ = null;
          console.error('Failed to load page settings', err);
          return throwError(() => err);
        })
      );
    }

    this.allSettingsFetch$.subscribe();
  }

  getPageSettings(pageName: string): Observable<PageSettingsData> {
    // 1. If we already have it in memory, return it instantly
    if (this.pageSettingsMap.has(pageName)) {
      return of(this.pageSettingsMap.get(pageName));
    }

    // 2. Otherwise, wait for the global API fetch to finish and return the result
    return this.settingsLoaded$.pipe(
      take(1),
      map(() => this.pageSettingsMap.get(pageName))
    );
  }

  extractEnvironmentConfig(): Record<string, any> {
    const envConfig = this.pageSettingsMap.get('environmentConfiguration');
    if (!envConfig?.settings) {
      return {};
    }
    return envConfig.settings.reduce(
      (acc: Record<string, any>, setting: any) => {
        acc[setting.fieldKey] = setting.value;
        return acc;
      },
      {}
    );
  }

  getServerConfig(): Observable<any> {
    return this.http.get(`${API_URL}/api/server-config`, {
      params: new HttpParams().set("includeMeta", "true"),
      headers: this.userService.getHttpAuthHeader(),
    });
  }

  updateServerConfig(updates: Record<string, any>): Observable<any> {
    return this.http.put(
      `${API_URL}/api/server-config`,
      { updates },
      {
        headers: this.userService.getHttpAuthHeader(),
      }
    );
  }

  updateConfigSettings(pageName: string, settings: ConfigSetting[]): Observable<any> {
    return this.customerService.fetchEnterpriseId().pipe(
      switchMap(enterpriseId => {
        const params = new HttpParams().set('enterpriseId', enterpriseId);
        const body: UpdateConfigRequest = { pageName, settings };
        return this.http.put(`${API_URL}${API_CONFIG_URL}`, body, {
          params,
          headers: this.userService.getHttpAuthHeader()
        }).pipe(
          tap((data: UpdateConfigResponse) => {
            const pageConfig = this.pageSettingsMap.get(pageName);
            const existing = pageConfig.settings ?? [];
            if (data.config.settings.length) {
              data.config.settings.forEach(newSetting => {
                const idx = existing.findIndex(s => s.fieldKey === newSetting.fieldKey);
                if (idx > -1) existing[idx] = { ...existing[idx], ...newSetting };
              });
              this.pageSettingsMap.set(pageName, { ...pageConfig, settings: existing });
              if (pageName === 'environmentConfiguration') {
                // Merge to preserve pre-login fields (like authTokenKey).
                // Without this, saving settings for a new tenant would wipe the runtime.
                environment.runtime = { ...environment.runtime, ...this.extractEnvironmentConfig() };
              }
            }
          })
        );
      })
    );
  }

  refreshEnvConfig(): Observable<any[]> {
    const headers = this.userService.getHttpAuthHeader();
    const pyapiCall = this.http.get(`${API_URL}/pyapi${REFRESH_ENV_CONFIG}`, { headers });
    const predictionCall = this.http.get(`${API_URL}/prediction${REFRESH_ENV_CONFIG}`, { headers });
    return forkJoin([pyapiCall, predictionCall]);
  }

  /**
   * Returns the status value at or above which alert accumulation stops,
   * derived dynamically from the `stopAppendingFailureModeByStatus` config.
   * Mirrors the backend logic in getFailureModeAppendStopStatus().
   *
   * Config value mapping:
   *   1 ("Default")                  → 4  (statuses >= 4 stop accumulating)
   *   2 ("In Process and On Watch")  → 2  (statuses >= 2 stop accumulating)
   *   3 ("On Watch")                 → 3  (statuses >= 3 stop accumulating)
   */
  getAccumulationStopStatus(): Observable<number> {
    return this.getPageSettings('alertsPage').pipe(
      map((data: PageSettingsData) => {
        const settings = data?.settings ?? [];
        const accumulationConfig = settings.find(s => s.fieldKey === 'stopAppendingFailureModeByStatus');
        if (!accumulationConfig) return 4;
        const configValue = accumulationConfig.value;
        const selectedOption = accumulationConfig.options?.find(o => o.value === configValue);
        switch (selectedOption?.value) {
          case 2: return 2; // In Process and On Watch
          case 3: return 3; // On Watch
          case 1: // Default
          default: return 4;
        }
      })
    );
  }

  isOnWatchStatusHidden(): Observable<boolean> {
    return this.getPageSettings('environmentConfiguration').pipe(
      map((data: PageSettingsData) => {
        const settings = data?.settings ?? [];
        const alertsConfig = settings.find(s => s.fieldKey === 'ALERT_STATUS_HIDING');
        return alertsConfig?.value === 1;
      })
    );
  }

  getShowAlertV2Flag(): boolean {
    return this.showAlertV2;
  }

  setShowAlertV2Flag(value: boolean) {
    this.showAlertV2 = value;
  }

  waitForSettings(): Promise<void> {
    return firstValueFrom(this.settingsLoaded$.pipe(take(1)));
  }

  isNewMemoryStructureEnabled(): boolean {
    return environment.runtime?.ENABLE_NEW_MEMORY_STRUCTURE === true;
  }

  isNewApiArchitectureEnabled(): boolean {
    return environment.runtime?.ENABLE_NEW_API_ARCHITECTURE === true;
  }

  getGenAIAccessValue(isExp: boolean = false): Observable<boolean> {
    if (isExp) {
      return of(true);
    }
    else if (environment.runtime.GENAI_ENABLED) {
      return this.getPageSettings("tenantModuleAccess").pipe(
        switchMap((tenantModuleAccessSettings: PageSettingsData) => {
          const settings = tenantModuleAccessSettings.settings;
          const config = settings?.find(s => s.fieldKey === "tenantModuleAccess");
          const hasTenantModuleAccess = config?.value?.includes(11);

          if (!hasTenantModuleAccess) return of(false);
          else {
            let userDetails = this.userService.getLoggedInUser();
            if (userDetails.moduleAccess.find(module => module.m === 11)) {
              return of(true)
            }
            return of(false);
          }
        })
      );
    } else {
      return of(false);
    }
  }
}